import React, { useState, useEffect } from 'react';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import { validatePassword, PasswordValidationResult } from '../utils/passwordValidator';

interface PasswordInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  showStrengthMeter?: boolean;
  className?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  onValidationChange,
  label = 'Mot de passe',
  placeholder = 'Entrez votre mot de passe',
  required = true,
  showStrengthMeter = true,
  className = ''
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationResult, setValidationResult] = useState<PasswordValidationResult>({ isValid: false, errors: [] });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    // Valider le mot de passe lorsqu'il change
    const result = validatePassword(value);
    setValidationResult(result);
    
    // Notifier le composant parent du changement de validation
    if (onValidationChange) {
      onValidationChange(result.isValid, result.errors);
    }
  }, [value, onValidationChange]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  return (
    <div className={`password-input-container ${className}`}>
      {label && (
        <label htmlFor="password">
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      
      <div className="password-input-wrapper">
        <input
          type={showPassword ? 'text' : 'password'}
          id="password"
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          className={`password-input ${touched && !validationResult.isValid ? 'invalid' : ''}`}
        />
        <button
          type="button"
          onClick={toggleShowPassword}
          className="toggle-password-button"
          tabIndex={-1}
        >
          {showPassword ? 'Cacher' : 'Afficher'}
        </button>
      </div>
      
      {showStrengthMeter && value && (
        <PasswordStrengthMeter password={value} />
      )}
      
      {touched && validationResult.errors.length > 0 && (
        <div className="validation-errors">
          <ul>
            {validationResult.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <style>
        {`
        .password-input-container {
          margin-bottom: 15px;
        }
        
        label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }
        
        .required {
          color: #f44336;
        }
        
        .password-input-wrapper {
          position: relative;
          display: flex;
        }
        
        .password-input {
          flex: 1;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 16px;
        }
        
        .password-input.invalid {
          border-color: #f44336;
        }
        
        .toggle-password-button {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          font-size: 14px;
        }
        
        .validation-errors {
          margin-top: 5px;
          color: #f44336;
          font-size: 14px;
        }
        
        .validation-errors ul {
          margin: 0;
          padding-left: 20px;
        }
        `}
      </style>
    </div>
  );
};

export default PasswordInput;
